import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { Move3D, LocateFixed, Globe, ChevronLeft, ChevronRight } from 'lucide-react';

export default function SphericalViewer() {
  const containerRef = useRef(null);
  const S = useRef({
    scene: null, camera: null, renderer: null, animId: null, mesh: null,
    isDragging: false, prevPos: { x: 0, y: 0 },
    lon: 180, lat: 0, tLon: 180, tLat: 0,
    gyroLonOff: 0, gyroLatOff: 0, tGyroLonOff: 0, tGyroLatOff: 0,
    pinchDist: 0, pinchFov: 75,
    gyroOn: false, devQ: new THREE.Quaternion(), screenOr: 0,
    gyroBaseInv: null,
  }).current;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gyroStatus, setGyroStatus] = useState('unknown');
  const [urls, setUrls] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const parsed = p.getAll('url').filter(u => u.trim());
    setUrls(parsed);
    setIdx(0);
    if ('DeviceOrientationEvent' in window) {
      setGyroStatus(typeof DeviceOrientationEvent.requestPermission === 'function' ? 'available' : 'active');
    } else {
      setGyroStatus('unsupported');
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const c = containerRef.current;
    const scene = new THREE.Scene();
    const cam = new THREE.PerspectiveCamera(75, c.clientWidth / c.clientHeight, 0.1, 1100);
    cam.position.set(0, 0, 0);
    const ren = new THREE.WebGLRenderer({ antialias: true });
    ren.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    ren.setSize(c.clientWidth, c.clientHeight);
    c.appendChild(ren.domElement);
    S.scene = scene; S.camera = cam; S.renderer = ren;

    const onResize = () => { cam.aspect = c.clientWidth / c.clientHeight; cam.updateProjectionMatrix(); ren.setSize(c.clientWidth, c.clientHeight); };
    window.addEventListener('resize', onResize);

    const applyDrag = (dx, dy) => {
      if (S.gyroOn) {
        S.tGyroLonOff -= dx; S.tGyroLatOff += dy;
        S.tGyroLatOff = Math.max(-85, Math.min(85, S.tGyroLatOff));
      } else {
        S.tLon -= dx; S.tLat += dy;
        S.tLat = Math.max(-85, Math.min(85, S.tLat));
      }
    };

    const el = ren.domElement;
    el.style.touchAction = 'none';
    const d2 = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    el.addEventListener('pointerdown', e => { S.isDragging = true; S.prevPos = { x: e.clientX, y: e.clientY }; });
    el.addEventListener('pointermove', e => { if (!S.isDragging) return; applyDrag((e.clientX - S.prevPos.x) * 0.15, (e.clientY - S.prevPos.y) * 0.15); S.prevPos = { x: e.clientX, y: e.clientY }; });
    const pUp = () => { S.isDragging = false; };
    el.addEventListener('pointerup', pUp); el.addEventListener('pointerleave', pUp); el.addEventListener('pointercancel', pUp);

    el.addEventListener('touchstart', e => { e.preventDefault(); if (e.touches.length === 1) { S.isDragging = true; S.prevPos = { x: e.touches[0].clientX, y: e.touches[0].clientY }; } else if (e.touches.length === 2) { S.isDragging = false; S.pinchDist = d2(e.touches); S.pinchFov = cam.fov; } }, { passive: false });
    el.addEventListener('touchmove', e => { e.preventDefault(); if (e.touches.length === 1 && S.isDragging) { applyDrag((e.touches[0].clientX - S.prevPos.x) * 0.2, (e.touches[0].clientY - S.prevPos.y) * 0.2); S.prevPos = { x: e.touches[0].clientX, y: e.touches[0].clientY }; } else if (e.touches.length === 2) { cam.fov = Math.max(20, Math.min(100, S.pinchFov * (S.pinchDist / d2(e.touches)))); cam.updateProjectionMatrix(); } }, { passive: false });
    el.addEventListener('touchend', e => { e.preventDefault(); S.isDragging = false; if (e.touches.length === 1) { S.isDragging = true; S.prevPos = { x: e.touches[0].clientX, y: e.touches[0].clientY }; } }, { passive: false });
    el.addEventListener('touchcancel', e => { e.preventDefault(); S.isDragging = false; }, { passive: false });
    el.addEventListener('wheel', e => { e.preventDefault(); cam.fov = Math.max(20, Math.min(100, cam.fov + e.deltaY * 0.05)); cam.updateProjectionMatrix(); }, { passive: false });

    const onOr = () => { S.screenOr = window.orientation || 0; };
    window.addEventListener('orientationchange', onOr);

    const animate = () => {
      S.animId = requestAnimationFrame(animate);
      if (S.gyroOn) {
        S.gyroLonOff += (S.tGyroLonOff - S.gyroLonOff) * 0.15;
        S.gyroLatOff += (S.tGyroLatOff - S.gyroLatOff) * 0.15;
        const q = S.devQ.clone();
        q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), -THREE.MathUtils.degToRad(S.screenOr)));
        if (S.gyroBaseInv) {
          q.premultiply(S.gyroBaseInv);
          const phi = THREE.MathUtils.degToRad(90), theta = THREE.MathUtils.degToRad(180);
          const t = new THREE.Vector3(500 * Math.sin(phi) * Math.cos(theta), 500 * Math.cos(phi), 500 * Math.sin(phi) * Math.sin(theta));
          q.premultiply(new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), t, new THREE.Vector3(0, 1, 0))));
        }
        q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -THREE.MathUtils.degToRad(S.gyroLonOff)));
        q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), THREE.MathUtils.degToRad(S.gyroLatOff)));
        cam.quaternion.copy(q);
      } else {
        S.lon += (S.tLon - S.lon) * 0.15;
        S.lat += (S.tLat - S.lat) * 0.15;
        const phi = THREE.MathUtils.degToRad(90 - S.lat), theta = THREE.MathUtils.degToRad(S.lon);
        cam.lookAt(500 * Math.sin(phi) * Math.cos(theta), 500 * Math.cos(phi), 500 * Math.sin(phi) * Math.sin(theta));
      }
      ren.render(scene, cam);
    };
    animate();

    return () => { cancelAnimationFrame(S.animId); window.removeEventListener('resize', onResize); window.removeEventListener('orientationchange', onOr); if (c.contains(ren.domElement)) c.removeChild(ren.domElement); ren.dispose(); };
  }, []);

  useEffect(() => {
    if (!S.scene || urls.length === 0) return;
    const url = urls[idx];
    if (!url) return;
    setLoading(true); setError(null);
    S.lon = 180; S.lat = 0; S.tLon = 180; S.tLat = 0;
    S.gyroLonOff = 0; S.gyroLatOff = 0; S.tGyroLonOff = 0; S.tGyroLatOff = 0;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(url, tex => {
      tex.colorSpace = THREE.SRGBColorSpace;
      if (S.mesh) { S.scene.remove(S.mesh); S.mesh.geometry.dispose(); S.mesh.material.dispose(); }
      const geo = new THREE.SphereGeometry(500, 64, 32); geo.scale(-1, 1, 1);
      S.mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: tex }));
      S.scene.add(S.mesh);
      setLoading(false);
    }, undefined, () => { setError('Failed to load image. Check CORS / URL.'); setLoading(false); });
  }, [idx, urls]);

  useEffect(() => {
    if (gyroStatus !== 'active') return;
    const mkQ = (() => {
      const zee = new THREE.Vector3(0, 0, 1), eu = new THREE.Euler();
      const q0 = new THREE.Quaternion(), q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));
      return (out, a, b, g, o) => { eu.set(b, a, -g, 'YXZ'); out.setFromEuler(eu); out.multiply(q1); q0.setFromAxisAngle(zee, -o); out.multiply(q0); };
    })();
    const handler = e => {
      if (e.alpha == null && e.beta == null && e.gamma == null) return;
      mkQ(S.devQ, THREE.MathUtils.degToRad(e.alpha || 0), THREE.MathUtils.degToRad(e.beta || 0), THREE.MathUtils.degToRad(e.gamma || 0), THREE.MathUtils.degToRad(S.screenOr));
      S.gyroOn = true;
    };
    window.addEventListener('deviceorientation', handler, true);
    return () => { window.removeEventListener('deviceorientation', handler, true); S.gyroOn = false; };
  }, [gyroStatus]);

  const enableGyro = useCallback(async () => {
    try {
      if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
        const p = await DeviceOrientationEvent.requestPermission();
        setGyroStatus(p === 'granted' ? 'active' : 'denied');
      } else { setGyroStatus('active'); }
    } catch { setGyroStatus('denied'); }
  }, []);

  const toggleGyro = useCallback(() => {
    if (gyroStatus === 'active') { setGyroStatus('available'); S.gyroOn = false; S.gyroLonOff = 0; S.gyroLatOff = 0; S.tGyroLonOff = 0; S.tGyroLatOff = 0; S.gyroBaseInv = null; }
    else enableGyro();
  }, [gyroStatus, enableGyro]);

  const recalibrate = useCallback(() => {
    const q = S.devQ.clone();
    q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), -THREE.MathUtils.degToRad(S.screenOr)));
    S.gyroBaseInv = q.invert();
    S.gyroLonOff = 0; S.gyroLatOff = 0; S.tGyroLonOff = 0; S.tGyroLatOff = 0;
  }, []);

  const goPrev = () => setIdx(i => (i - 1 + urls.length) % urls.length);
  const goNext = () => setIdx(i => (i + 1) % urls.length);

  const hasImages = urls.length > 0;
  const multiImg = urls.length > 1;

  const iconBtn = (active) => ({
    background: active ? 'rgba(59,130,246,0.85)' : 'rgba(0,0,0,0.55)',
    color: '#fff', border: '1px solid rgba(255,255,255,0.12)',
    width: 44, height: 44, borderRadius: 12, cursor: 'pointer',
    backdropFilter: 'blur(8px)', transition: 'all 0.2s',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  });

  const navBtn = {
    background: 'rgba(0,0,0,0.55)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.12)',
    width: 48, height: 48, borderRadius: 14, cursor: 'pointer',
    backdropFilter: 'blur(8px)', transition: 'all 0.2s',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100dvh', background: '#000', overflow: 'hidden' }}>

      {/* Layer 1: Three.js canvas */}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

      {/* Layer 2: UI overlay */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', display: 'flex', flexDirection: 'column', zIndex: 5, paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: 12, pointerEvents: 'none', flexShrink: 0 }}>
          {hasImages && !loading && !error ? (
            <div style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', backdropFilter: 'blur(8px)', borderRadius: 10, fontSize: 12, lineHeight: 1.5, padding: '10px 14px', pointerEvents: 'auto' }}>
              Drag to look • Scroll/pinch to zoom
              {multiImg && <span style={{ opacity: 0.6 }}> • {idx + 1}/{urls.length}</span>}
            </div>
          ) : <div />}
          {hasImages && !loading && !error && gyroStatus !== 'unsupported' && (
            <div style={{ display: 'flex', gap: 8, pointerEvents: 'auto' }}>
              {gyroStatus === 'active' && (
                <button onClick={recalibrate} style={iconBtn(false)} title="Level / recalibrate">
                  <LocateFixed size={20} />
                </button>
              )}
              <button onClick={toggleGyro} style={iconBtn(gyroStatus === 'active')} title="Toggle gyroscope">
                <Move3D size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Bottom bar */}
        {hasImages && !loading && !error && multiImg && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px 16px 16px', flexShrink: 0, pointerEvents: 'none' }}>
            <button onClick={goPrev} style={{ ...navBtn, pointerEvents: 'auto' }} title="Previous">
              <ChevronLeft size={24} />
            </button>
            <div style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', backdropFilter: 'blur(8px)', borderRadius: 10, fontSize: 14, padding: '8px 16px', fontVariantNumeric: 'tabular-nums', pointerEvents: 'auto' }}>
              {idx + 1} / {urls.length}
            </div>
            <button onClick={goNext} style={{ ...navBtn, pointerEvents: 'auto' }} title="Next">
              <ChevronRight size={24} />
            </button>
          </div>
        )}
      </div>

      {/* Layer 3: Modal overlays (loading / error / empty) */}
      {!hasImages && !loading && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          <div style={{ color: '#fff', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}><Globe size={48} style={{ opacity: 0.4 }} /></div>
            <div style={{ fontSize: 16, marginBottom: 8, opacity: 0.6 }}>No image provided</div>
            <div style={{ fontSize: 13, opacity: 0.4 }}>Add <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: 4 }}>?url=…</code> to the URL</div>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', zIndex: 10 }}>
          <div style={{ textAlign: 'center', color: '#fff' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
            <div style={{ fontSize: 16 }}>Loading{multiImg ? ` ${idx + 1} of ${urls.length}` : ''}…</div>
          </div>
        </div>
      )}

      {error && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', zIndex: 10, padding: 24 }}>
          <div style={{ color: '#fff', textAlign: 'center', maxWidth: 400 }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>⚠️ Error</div>
            <div style={{ fontSize: 14, opacity: 0.8 }}>{error}</div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}